import { createElement, useCallback, useContext, useEffect, useId, useState, type ReactNode } from "react";
import { BackNavigationContext } from "@/contexts/BackNavigationContext";
import { DiscardChangesDialog } from "@/components/form/DiscardChangesDialog";

/**
 * Covers the exits the browser owns (tab close, reload, address-bar
 * navigation) by registering a `beforeunload` listener while `when` is true.
 *
 * In-app exits are guarded by `useDirtyGuard().confirmLeave` at the exits each
 * page owns (Cancel, Escape, a back link, a tab switch). `react-router`'s
 * `useBlocker` is deliberately not used: `src/main.tsx` mounts a plain
 * `<BrowserRouter>`, not a data router, and `useBlocker` throws outside one.
 */
export function useUnsavedChangesWarning(when: boolean): void {
  useEffect(() => {
    if (!when) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // preventDefault plus a non-empty returnValue is what every current
      // browser needs to show its own generic "Leave site?" prompt. The text
      // itself is not customizable and is never rendered from here.
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when]);
}

export interface DirtyGuard {
  /** Runs `next` at once when clean; otherwise asks first and runs it on "Discard". */
  confirmLeave: (next: () => void) => void;
  /** The discard dialog; render it once, anywhere in the guarded tree. */
  dialog: ReactNode;
}

/**
 * The unsaved-changes guard for a form or page: arms `beforeunload` while
 * `isDirty`, and gates in-app exits behind the discard dialog.
 */
export function useDirtyGuard(isDirty: boolean): DirtyGuard {
  useUnsavedChangesWarning(isDirty);
  // The exit the user asked for, held while the dialog is open.
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);

  const confirmLeave = useCallback(
    (next: () => void) => {
      if (!isDirty) {
        next();
        return;
      }
      // Functional form: a bare function passed to a setter would be called.
      setPendingLeave(() => next);
    },
    [isDirty],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) setPendingLeave(null);
  }, []);

  const handleConfirm = useCallback(() => {
    setPendingLeave(null);
    pendingLeave?.();
  }, [pendingLeave]);

  // The shell's Back button (top bar) is an exit the page does not own: hand it
  // the guard while dirty, so it asks like Cancel and Escape do. No-op outside
  // the shell (tests, the portal).
  const backNavigation = useContext(BackNavigationContext);
  const registerLeaveGuard = backNavigation?.registerLeaveGuard;
  const guardId = useId();
  useEffect(() => {
    if (!registerLeaveGuard || !isDirty) return;
    registerLeaveGuard(guardId, confirmLeave);
    return () => registerLeaveGuard(guardId, null);
  }, [registerLeaveGuard, guardId, isDirty, confirmLeave]);

  const dialog = createElement(DiscardChangesDialog, {
    open: pendingLeave !== null,
    onOpenChange: handleOpenChange,
    onConfirm: handleConfirm,
  });

  return { confirmLeave, dialog };
}
